import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputProps,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { forwardRef, useEffect, useState } from 'react';
import { FiPhone } from 'react-icons/fi';
import type { Value as E164Number } from 'react-phone-number-input';
import PhoneInput, {
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input/input';

export interface DialOutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (phoneNumber: string) => void;
  defaultPhoneNumber?: string | null;
  patientName?: string;
  error?: string | null;
  /** Whether a dial-out call is currently being initiated */
  isCalling?: boolean;
}

/**
 * Chakra-styled input component for use with react-phone-number-input.
 * Uses forwardRef as required by the library.
 */
const ChakraPhoneInput = forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => (
    <Input ref={ref} {...props} data-testid="dialout-phone-input" autoFocus />
  ),
);
ChakraPhoneInput.displayName = 'ChakraPhoneInput';

/**
 * Confirmation modal for initiating a PSTN dial-out call.
 *
 * Displays an explanation of the feature and allows the provider
 * to confirm or edit the phone number before calling.
 *
 * Uses react-phone-number-input for automatic US phone formatting.
 * Displays: "(212) 555-1234" (national format)
 * Outputs:  "+12125551234" (E.164 format for Daily API)
 *
 * Dial-out is enabled proactively during room verification (before the call starts),
 * so this modal is a pure phone-input UI.
 */
export const DialOutModal: React.FC<DialOutModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  defaultPhoneNumber = '',
  patientName,
  error = null,
  isCalling = false,
}) => {
  const [phoneNumber, setPhoneNumber] = useState<E164Number | undefined>(
    (defaultPhoneNumber || undefined) as E164Number | undefined,
  );

  // Reset phone number when modal opens with new default
  useEffect(() => {
    if (isOpen) {
      // parsePhoneNumber throws on non-string input, and defaultPhoneNumber is
      // null when the patient has no phone on file — guard to a real string.
      setPhoneNumber(
        defaultPhoneNumber
          ? parsePhoneNumber(defaultPhoneNumber, 'US')?.number
          : undefined,
      );
    }
  }, [isOpen, defaultPhoneNumber]);

  // Validate using libphonenumber-js (built into react-phone-number-input)
  const isValidPhone = phoneNumber ? isValidPhoneNumber(phoneNumber) : false;

  const handleConfirm = () => {
    if (phoneNumber && isValidPhone && !isCalling) {
      onConfirm(phoneNumber);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValidPhone && !isCalling) {
      handleConfirm();
    }
  };

  const isButtonDisabled = !isValidPhone || isCalling;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent
        bg="surface.default"
        borderColor="border.default"
        borderWidth={1}
        borderRadius={10}
        color="text.default"
      >
        <ModalHeader display="flex" alignItems="center" gap={2} mt={2}>
          <FiPhone />
          Call Patient by Phone
        </ModalHeader>

        <ModalBody as={VStack} spacing={4} alignItems="stretch">
          <Text color="text.muted" fontSize="sm">
            If the patient is having technical difficulties joining the video
            call, you can call their phone instead. They will be connected to
            this call via phone audio.
          </Text>

          {error && (
            <Alert status="error" borderRadius="md" data-testid="dialout-error">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <FormControl>
            <FormLabel fontSize="sm">
              Number to call
              {patientName && (
                <Text as="span" color="text.muted" fontWeight="normal">
                  {' '}
                  (The default value here is {patientName.trim()}&apos;s phone.)
                </Text>
              )}
            </FormLabel>
            <PhoneInput
              country="US"
              value={phoneNumber}
              onChange={setPhoneNumber}
              onKeyDown={handleKeyDown}
              inputComponent={ChakraPhoneInput}
              disabled={isCalling}
            />
          </FormControl>
        </ModalBody>

        <ModalFooter justifyContent="center" gap={4} mb={4}>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="dialout-cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            bg="text.brand"
            color="white"
            onClick={handleConfirm}
            isDisabled={isButtonDisabled}
            isLoading={isCalling}
            loadingText="Calling..."
            leftIcon={<FiPhone />}
            data-testid="dialout-confirm-button"
            _hover={{ bg: 'green.600' }}
          >
            Call
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
